import { createMutator, type TransportOptions } from '@hurdl/http-client';

/**
 * The mutator `@hurdl/http-client` builds, which does the whole of the request work:
 * it strips the transport out of an operation's options so the transport never reaches
 * `fetch`, and raises the message below where an operation was called with none.
 *
 * The message names this client and one of its operations, which is what makes it
 * useful, and it is supplied here rather than in the Platform Package for the reason
 * that package names no consumer of its own.
 */
const mutator = createMutator({
  absentTransportMessage:
    'No transport was supplied to the menus review client. Pass one as the `transport` property of the operation options, for example `listReviewQueue({ transport })`.',
});

/**
 * The seam between the Generated_Client and the transport the composing application
 * supplies.
 *
 * Every generated operation calls {@link sendRequest} and nothing else, which is what
 * keeps `src/client/generated/` free of a `fetch` call and of a base address: the
 * operation knows its own path and method, and the transport it is handed knows where
 * the review router is mounted. The transport arrives as a property of the operation's
 * trailing options rather than as module-level state, so one page may hold two clients
 * against two mount points.
 *
 * It is a declared function rather than the factory's return value because Orval reads
 * the declaration: it types each operation's trailing parameter as
 * `Parameters<typeof sendRequest>[1]`, and only where it can see a second parameter
 * here. Exported as the factory's return value it would emit `options?: RequestInit`
 * instead, a different generated file and a narrower type with no `transport` property.
 *
 * @throws Error when no transport was supplied, which is a wiring defect rather than a
 *   request failure and so carries no status and no platform error code.
 */
export async function sendRequest<TPayload>(
  url: string,
  options: TransportOptions,
): Promise<TPayload> {
  return await mutator<TPayload>(url, options);
}
